import React, { useEffect, useRef, useState, useMemo } from "react";
import { Drawer, Menu, Button, Input } from "antd";
import {
  MenuOutlined, LogoutOutlined, ArrowLeftOutlined, SearchOutlined,
} from "@ant-design/icons";
import { Outlet, useLocation, useNavigate, useMatches } from "react-router-dom";
import { useUI } from "@/context/UIContext";

export default function MobileLayout() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const matches = useMatches();

  const { searchOpen, searchQuery, openSearch, closeSearch, setSearchQuery, headerTitle } = useUI();

  // читаем меню из router.handle.menu
  const routeMenu = useMemo(() => {
    const found = matches.find((m) => m.handle?.menu)?.handle?.menu;
    return Array.isArray(found) ? found : null;
  }, [matches]);

  // Преобразуем структуру handle.menu в items Ant Menu
  const buildMenuItems = (menu = []) =>
    menu.map((m, idx) => {
      if (m.children && m.children.length) {
        return {
          key: `group-${idx}`,
          icon: m.icon,
          label: m.label,
          children: m.children.map((c) => ({
            key: c.to,
            icon: c.icon,
            label: c.label,
          })),
        };
      }
      return {
        key: m.to,
        icon: m.icon,
        label: m.label,
      };
    });

  const items = [
    ...(buildMenuItems(routeMenu || [])),
    { type: "divider" },
    { key: "/logout", icon: <LogoutOutlined />, label: "Вийти" },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === "/logout") {
      localStorage.removeItem("auth_user");
      localStorage.removeItem("auth_token");
      setOpen(false);
      nav("/login", { replace: true });
      return;
    }
    // если клик по группе — ничего не делаем
    if (key?.startsWith?.("group-")) return;

    setOpen(false);
    nav(key);
  };

  const inputRef = useRef(null);
  useEffect(() => {
    if (searchOpen) {
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [searchOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-base-200">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-12 bg-white border-b sticky top-0 z-20">
        <Button type="text" icon={<MenuOutlined />} onClick={() => setOpen(true)} aria-label="Меню" />
        <div className="font-semibold">{headerTitle}</div>
        <Button type="text" icon={<SearchOutlined />} onClick={openSearch} aria-label="Пошук" />
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, height: 48, background: "#fff",
            display: "flex", alignItems: "center", padding: "0 12px", gap: 8,
            zIndex: 2000, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={closeSearch} aria-label="Закрити пошук" />
          <Input
            ref={inputRef}
            allowClear
            size="middle"
            placeholder="Пошук по назві…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
        </div>
      )}

      {/* Page content */}
      <div className="flex-1 overflow-auto pb-16">
        <Outlet />
      </div>

      {/* Drawer menu */}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="left"
        width={280}
        closable={false}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ padding: 0 }}
        title={null}
      >
        <div
          className="flex items-center px-4 py-3 border-b cursor-pointer select-none"
          onClick={() => setOpen(false)}
        >
          <Button type="text" icon={<ArrowLeftOutlined />} />
          <div className="ml-2 text-lg font-semibold">Меню</div>
        </div>

        <Menu
          mode="inline"
          onClick={handleMenuClick}
          selectedKeys={[pathname]}
          items={items}
        />
      </Drawer>
    </div>
  );
}
